import React, { useState, useEffect, useCallback } from "react";
import {
  DashboardOutlined,
  MenuOutlined, BulbOutlined, BulbFilled, SettingOutlined, RadarChartOutlined, ReadOutlined,
  InboxOutlined, DownOutlined, RightOutlined,
  NodeIndexOutlined, LogoutOutlined, CommentOutlined, CloseOutlined,
  FileAddOutlined, FileTextOutlined,
  BarChartOutlined, DollarOutlined, TeamOutlined, ContainerOutlined, BgColorsOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Badge, Grid } from "antd";
import { useThemeMode } from "../theme/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { startTracker, trackPage } from "../utils/activityTracker";
import GuideButton from "../help/GuideDrawer";
import NotificationBell from "./NotificationBell";
import MaintenanceWatcher from "./MaintenanceWatcher";
import { getConversations, getAppVersion } from "../api/client";
import { newInvoicePath } from "../pages/supplychain/supplyCategories";
import { useNavigationGuard } from "../hooks/useNavigationGuard";

const { useBreakpoint } = Grid;
/** نسخه از بک‌اند خوانده می‌شود؛ رشتهٔ دستی در بروزرسانی‌ها جا می‌ماند و
 *  کاربر نسخهٔ قدیمی می‌دید در حالی که سرور جدید بود. این مقدار فقط تا
 *  رسیدنِ پاسخ نشان داده می‌شود. */
const FALLBACK_VERSION = "—";
const OPEN_GROUPS_KEY = "gx-nav-open-groups";

// منوی سه‌سطحی — «تولید» مادرِ تولید/پرسنل/حقوق؛ زنجیره تأمین جدا
const NAV = [
  { key: "/dashboard", icon: <DashboardOutlined />, label: "داشبورد", perm: "dashboard.view" },
  {
    id: "factory", icon: <InboxOutlined />, label: "تولید",
    children: [
      {
        id: "production", icon: <InboxOutlined />, label: "تولید و بسته‌بندی",
        perm: "production.view", children: [
          { key: "/production/cut", label: "ثبت برش", perm: "production.view" },
          { key: "/production/waste", label: "ثبت ضایعات", perm: "production.view" },
          { key: "/production", label: "ثبت و مدیریت تولید", perm: "production.view" },
          // نرخ‌نامه سراسر قیمت است؛ کسی که مبالغِ تولید را نمی‌بیند نباید ببیندش
          /* 🐞 حسابدار تیکِ «مدیریتِ نرخ‌نامه» را داشت ولی منو/مسیر فقط «مشاهدهٔ مبالغ → تولید» را
             می‌خواست ⇒ صفحه برایش نمی‌آمد. هر کدام از مجوزهای نرخ‌نامه کافی است. */
          { key: "/piece-rate", label: "نرخ‌نامهٔ دوخت", perm: "amounts.view_production",
            anyOf: ["production.rates.manage", "production.rates.create", "production.view_amounts", "amounts.view_production", "amounts.view_all"] },
        ],
      },
      {
        id: "personnel", icon: <TeamOutlined />, label: "پرسنل", perm: "personnel.view", children: [
          { key: "/employees", label: "لیست پرسنل", perm: "personnel.view" },
          { key: "/departments", label: "بخش‌ها / واحدها", perm: "personnel.view" },
          { key: "/employee-import", label: "ورود از اکسل", perm: "personnel.edit" },
        ],
      },
      /* محاسبهٔ حقوق و «حساب و کتاب» دو کارِ جدا با دو مخاطبِ جدا هستند
         (خواستهٔ کاربر): اولی ماه‌به‌ماه تمام می‌شود، دومی دفترِ همیشگیِ
         بدهکار/بستانکار است. گزارش‌ها هم داخلِ حساب و کتاب‌اند نه صفحهٔ جدا. */
      {
        id: "payroll", icon: <DollarOutlined />, label: "محاسبهٔ حقوق", perm: "payroll.view", children: [
          { key: "/attendance", label: "حضور و غیاب", perm: "attendance.view" },
          { key: "/payroll", label: "محاسبه و بستن حقوق", perm: "payroll.view" },
          { key: "/insurance", label: "بیمه (دو مرحله)", perm: "accounting.view" },
          // رانندهٔ کارمزدی: فی per مأموریت × تعدادِ دفعاتِ ماه
          { key: "/drivers", label: "راننده‌ها و مأموریت‌ها", perm: "payroll.view" },
        ],
      },
      {
        id: "accounting", icon: <DollarOutlined />, label: "حساب و کتاب", perm: "accounting.view", children: [
          { key: "/finance?tab=balances", label: "مانده و وضعیت حساب", perm: "accounting.view" },
          { key: "/finance?tab=advances", label: "مساعده", perm: "accounting.edit" },
          { key: "/finance?tab=payments", label: "پرداخت حقوق", perm: "accounting.view" },
          { key: "/finance?tab=txns", label: "هزینه‌ها و تراکنش‌ها", perm: "accounting.view" },
          { key: "/finance?tab=ledger", label: "دفتر حساب شخص", perm: "accounting.view" },
          { key: "/finance?tab=reports", label: "گزارش‌ها", perm: "reports.view" },
        ],
      },
    ],
  },
  {
    id: "supply-chain", icon: <NodeIndexOutlined />, label: "زنجیره تأمین", perm: "supply_chain.view", children: [
      { key: "/supply-chain", label: "داشبورد", perm: "supply_chain.view" },
      {
        id: "sc-new", key: "/supply-chain/new", label: "ثبت فاکتور", icon: <FileAddOutlined />,
        perm: "supply_chain.create", children: [
          { key: newInvoicePath("yarn_purchase"), label: "خرید نخ", perm: "supply_chain.create" },
          { key: newInvoicePath("knitting"), label: "بافندگی", perm: "supply_chain.create" },
          { key: newInvoicePath("dyeing"), label: "رنگرزی", perm: "supply_chain.create" },
          { key: "/supply-chain/import", label: "ایمپورت فایل", perm: "supply_chain.import" },
        ],
      },
      { key: "/supply-chain/events", label: "فاکتورها", icon: <FileTextOutlined />, perm: "supply_chain.view" },
      { key: "/supply-chain/parties", label: "طرف‌حساب‌ها و شرکت‌ها", icon: <TeamOutlined />, perm: "supply_chain.view" },
      {
        id: "sc-ops", label: "عملیات و گزارش‌ها", icon: <BarChartOutlined />, perm: "supply_chain.view", children: [
          { key: "/supply-chain/freight", label: "حمل و بارنامه", perm: "supply_chain.view" },
          { key: "/supply-chain/scale-discrepancy", label: "اختلاف باسکولی", perm: "supply_chain.view" },
          { key: "/supply-chain/batches", label: "ردیابی", perm: "supply_chain.view" },
          { key: "/supply-chain/custody", label: "دفتر امانی", perm: "supply_chain.view" },
          { key: "/supply-chain/opening-balance", label: "مانده اول دوره", perm: "supply_chain.view" },
          { key: "/supply-chain/cancelled", label: "فاکتورهای لغوشده", perm: "supply_chain.view" },
          { key: "/supply-chain/reports", label: "گزارش‌ها", perm: "supply_chain.view" },
        ],
      },
    ],
  },
  // اموال — بخشِ جدا با دسترسیِ انحصاری (assets.view فقط با تیکِ صریح)
  { key: "/assets", icon: <ContainerOutlined />, label: "اموال", perm: "assets.view" },
  // پیگیری رنگرزی — بخشِ جدا با دسترسیِ انحصاری (dye.view فقط با تیکِ صریح)
  { key: "/dye", icon: <BgColorsOutlined />, label: "پیگیری رنگرزی", perm: "dye.view" },
  { key: "/chat", icon: <CommentOutlined />, label: "گفتگو", perm: "notes.view", badge: "chat" },
  // v2.146.0 — راهنمای برنامه (همه می‌بینند)
  { key: "/guide", icon: <ReadOutlined />, label: "راهنمای برنامه" },
  // v2.142.0 — فقط ادمینِ اصلی: رفتارِ لحظه‌ایِ کاربران + وضعیتِ سرور
  { key: "/control-center", icon: <RadarChartOutlined />, label: "مرکز کنترل", superOnly: true },
  { key: "/settings", icon: <SettingOutlined />, label: "تنظیمات", perm: "settings.view" },
];

/** جمع‌آوری برگ‌ها + زنجیرهٔ والدین برای باز نگه‌داشتن گروه‌ها */
function collectLeaves(nodes, ancestors = [], out = []) {
  (nodes || []).forEach((n) => {
    const chain = n.id ? [...ancestors, n.id] : ancestors;
    const parentId = ancestors.length ? ancestors[ancestors.length - 1] : null;
    if (n.children) {
      if (n.key) out.push({ ...n, parent: parentId, ancestors: chain, _parentNav: true });
      collectLeaves(n.children, chain, out);
    } else if (n.key) {
      out.push({ ...n, parent: parentId, ancestors: chain });
    }
  });
  return out;
}

const LEAVES = collectLeaves(NAV);

function pathOf(key) {
  return String(key || "").split("?")[0];
}

function findCurrent(pathname, search = "") {
  const full = pathname + (search || "");
  const qs = new URLSearchParams(search || "");
  let best = null;
  for (const l of LEAVES) {
    const base = pathOf(l.key);
    // workspace جزئیات فاکتور
    if (pathname.startsWith("/supply-chain/events/") && l.key === "/supply-chain/events") {
      const score = 200;
      if (!best || score > (best._score || 0)) best = { ...l, label: "فاکتور", _score: score };
      continue;
    }
    if (pathname === base || pathname.startsWith(base + "/")) {
      let score = base.length;
      if (l.key.includes("?")) {
        const q = l.key.split("?")[1] || "";
        if (full.includes(q)) score += 80;
        else continue; // query مطابقت ندارد
      } else if (base === "/supply-chain/new" && l._parentNav) {
        // Landing فقط وقتی category در query نباشد
        if (qs.get("category")) continue;
        score += 40;
      }
      if (!best || score > (best._score || 0)) best = { ...l, _score: score };
    }
  }
  if (!best && pathname.startsWith("/supply-chain")) {
    return LEAVES.find((l) => l.key === "/supply-chain/events") || LEAVES[0];
  }
  return best || LEAVES[0];
}

function loadOpenGroups(fallback) {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return fallback;
}

export default function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const { mode, toggle } = useThemeMode();
  const { user, isAuthed, signOut, can } = useAuth();
  const [appVersion, setAppVersion] = useState(FALLBACK_VERSION);

  useEffect(() => {
    if (!isAuthed) return;
    getAppVersion()
      .then((r) => setAppVersion(String(r.data.semver)))
      .catch(() => {});
  }, [isAuthed]);
  // نشانگرِ اتصال در پای منو (آنلاین/آفلاینِ مرورگر)
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  // تا رسیدنِ پاسخِ سرور «—» است؛ پیشوندِ v فقط برای نسخهٔ واقعی
  const versionLabel = appVersion === FALLBACK_VERSION ? appVersion : `v${appVersion}`;
  const { tryNavigate } = useNavigationGuard();
  const [collapsed, setCollapsed] = useState(false);
  const [open, setOpen] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);

  const isMobile = !screens.md;
  // روی موبایل همیشه منوی کامل (کشویی)؛ حالتِ فقط‌آیکن فقط دسکتاپ
  const showCollapsed = isMobile ? false : collapsed;
  const current = findCurrent(location.pathname, location.search);
  // v2.142.0 — ردیابی برای مرکزِ کنترل: هر صفحه با نامِ منو‌یش
  useEffect(() => { if (isAuthed) startTracker(); }, [isAuthed]);
  useEffect(() => {
    if (isAuthed) trackPage(location.pathname + location.search, current.label);
  }, [isAuthed, location.pathname, location.search]);   // eslint-disable-line

  useEffect(() => {
    if (!isMobile || !open) return undefined;
    const h = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [isMobile, open]);

  const [openGroups, setOpenGroups] = useState(() => {
    const init = current.ancestors?.length
      ? [...current.ancestors]
      : (current.parent ? [current.parent] : []);
    if (!init.includes("factory")) init.push("factory");
    if (!init.includes("supply-chain")) init.push("supply-chain");
    return loadOpenGroups(init);
  });

  // باز کردن همهٔ والدین مسیر جاری
  useEffect(() => {
    const need = current.ancestors?.length
      ? current.ancestors
      : (current.parent ? [current.parent] : []);
    if (!need.length) return;
    setOpenGroups((g) => {
      let next = [...g];
      let changed = false;
      need.forEach((id) => {
        if (!next.includes(id)) { next.push(id); changed = true; }
      });
      if (!changed) return g;
      try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)); } catch { /* */ }
      return next;
    });
  }, [current.key, current.parent]);

  const toggleGroup = (id) => setOpenGroups((g) => {
    const next = g.includes(id) ? g.filter((x) => x !== id) : [...g, id];
    try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)); } catch { /* */ }
    return next;
  });

  const ensureOpen = (id) => setOpenGroups((g) => {
    if (g.includes(id)) return g;
    const next = [...g, id];
    try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)); } catch { /* */ }
    return next;
  });

  const go = (key) => {
    tryNavigate(key, (dest) => {
      navigate(dest);
      if (isMobile) setOpen(false);
    });
  };
  const onToggle = () => (isMobile ? setOpen((o) => !o) : setCollapsed((c) => !c));
  const allowed = (item) => {
    if (item.superOnly) return user?.role === "super_admin";
    if (Array.isArray(item.anyOf) && item.anyOf.length) return item.anyOf.some((p) => can(p));
    return !item.perm || can(item.perm);
  };

  // Badge گفتگو
  const refreshUnread = useCallback(() => {
    if (!can("notes.view")) return;
    getConversations().then((r) => {
      const n = (r.data || []).reduce((s, c) => s + (Number(c.unread) || 0), 0);
      setChatUnread(n);
    }).catch(() => {});
  }, [can]);
  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 30000);
    return () => clearInterval(t);
  }, [refreshUnread]);

  const isLeafActive = (leaf) => {
    const base = pathOf(leaf.key);
    if (location.pathname !== base && !location.pathname.startsWith(base + "/")) return false;
    if (leaf.key.includes("?")) {
      const q = leaf.key.split("?")[1] || "";
      return location.search.includes(q);
    }
    if (base === "/supply-chain/new") {
      const cat = new URLSearchParams(location.search).get("category");
      // leaf بدون query = Landing؛ با category در URL نباید active کامل باشد
      return location.pathname === "/supply-chain/new" && !cat;
    }
    if (base === "/supply-chain/events" && location.pathname.startsWith("/supply-chain/events/")) {
      return true;
    }
    return current.key === leaf.key || pathOf(current.key) === base;
  };

  const levelClass = (depth) => {
    if (depth <= 0) return "";
    if (depth === 1) return " gx-nav-l2";
    return " gx-nav-l3";
  };

  const renderLeaf = (c, depth = 0) => {
    if (!allowed(c)) return null;
    const active = isLeafActive(c);
    return (
      <button key={c.key}
        className={"gx-nav-item" + levelClass(depth) + (active ? " gx-nav-active" : "")}
        onClick={() => go(c.key)} title={showCollapsed ? c.label : undefined}>
        {depth === 0 && c.icon}
        {!showCollapsed && <span className="gx-nav-label">{c.label}</span>}
        {!showCollapsed && c.badge === "chat" && chatUnread > 0 && (
          <Badge count={chatUnread} size="small" style={{ marginInlineStart: 6 }} />
        )}
        {!showCollapsed && active && <span className="gx-nav-bar" />}
      </button>
    );
  };

  const renderGroup = (n, depth = 0) => {
    const kids = (n.children || []).filter((c) => {
      if (c.children) {
        return (c.children || []).some(allowed) || (c.key && allowed(c));
      }
      return allowed(c);
    });
    if (!kids.length) return null;
    const expanded = openGroups.includes(n.id);
    const hasActiveChild = kids.some((c) => {
      if (c.children) return (c.children || []).some((x) => isLeafActive(x)) || (c.key && isLeafActive(c));
      return isLeafActive(c);
    });
    const landingActive = n.key && isLeafActive(n);

    if (showCollapsed && depth === 0) {
      const findFirstLeaf = (nodes) => {
        for (const c of nodes || []) {
          if (c.children) {
            const deep = findFirstLeaf(c.children);
            if (deep) return deep;
            if (c.key) return c;
          } else if (c.key) return c;
        }
        return n.key ? n : null;
      };
      const firstLeaf = findFirstLeaf(kids);
      return (
        <button key={n.id} className={"gx-nav-item" + (hasActiveChild || landingActive ? " gx-nav-active" : "")}
          onClick={() => firstLeaf && go(firstLeaf.key)} title={n.label}>
          {n.icon}
        </button>
      );
    }

    const onParentText = () => {
      if (n.key) {
        ensureOpen(n.id);
        go(n.key);
      } else {
        toggleGroup(n.id);
      }
    };

    return (
      <div key={n.id} data-nav-group={n.id}
        className={"gx-nav-block" + (depth === 0 ? " gx-nav-block-root" : " gx-nav-block-nested")}>
        <button
          type="button"
          className={
            "gx-nav-item gx-nav-group"
            + (depth === 0 ? " gx-nav-root" : "")
            + levelClass(depth)
            + (hasActiveChild || landingActive ? " gx-nav-parent-active" : "")
            + (landingActive ? " gx-nav-landing-active" : "")
          }
          onClick={onParentText}
        >
          {depth === 0 ? n.icon : <span className="gx-nav-dot" aria-hidden="true" />}
          <span className="gx-nav-label">{n.label}</span>
          <span
            className="gx-nav-caret"
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); toggleGroup(n.id); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                toggleGroup(n.id);
              }
            }}
          >
            {expanded ? <DownOutlined /> : <RightOutlined />}
          </span>
        </button>
        {expanded && (
          <div className={"gx-nav-subwrap" + (depth === 0 ? " gx-nav-subwrap-root" : "")}>
            {kids.map((c) => (c.children ? renderGroup(c, depth + 1) : renderLeaf(c, depth + 1)))}
          </div>
        )}
      </div>
    );
  };

  const renderItem = (n) => {
    if (!n.children) {
      if (!allowed(n)) return null;
      return renderLeaf(n, false);
    }
    return renderGroup(n, 0);
  };

  return (
    <div className={"gx-app" + (showCollapsed ? " gx-collapsed" : "")}>
      {isMobile && open && <button className="gx-backdrop" onClick={() => setOpen(false)} aria-label="بستن" />}

      <aside className={"gx-sidebar" + (showCollapsed ? " gx-sidebar-collapsed" : "") + (open ? " gx-open" : "")}>
        <div className="gx-brand">
          <div className="gx-brand-mark">ق</div>
          {!showCollapsed && (
            <div className="gx-brand-text">
              <div className="gx-brand-name">قرچک فکتوری</div>
              <div className="gx-brand-tag">سیستم حقوق‌ودستمزد</div>
            </div>
          )}
          {isMobile && (
            <button type="button" className="gx-brand-close" onClick={() => setOpen(false)} aria-label="بستن منو" title="بستن">
              <CloseOutlined />
            </button>
          )}
        </div>

        <nav className="gx-nav">{NAV.map(renderItem)}</nav>

        {showCollapsed ? (
          <div className="gx-sidebar-foot" title="نسخهٔ برنامه">{versionLabel}</div>
        ) : (
          <>
            <div className="gx-sidebar-user">
              <span className="gx-sidebar-user-name" title={isAuthed && user ? user.full_name : undefined}>
                {isAuthed && user ? user.full_name : "مهمان"}
              </span>
              {isAuthed && (
                <button type="button" className="gx-sidebar-logout" onClick={signOut} title="خروج از حساب">
                  <LogoutOutlined /> خروج
                </button>
              )}
            </div>
            <div className="gx-sidebar-meta">
              <span className="gx-sidebar-version" title="نسخهٔ نصب‌شده">{versionLabel}</span>
              <span className={"gx-sidebar-mode " + (online ? "is-online" : "is-offline")}
                title={online ? "اتصال به سرور برقرار است" : "مرورگر آفلاین است"}>
                <i aria-hidden="true" />{online ? "اتصال فعال" : "آفلاین"}
              </span>
            </div>
          </>
        )}
      </aside>

      <div className="gx-main">
        <header className="gx-topbar">
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <button className="gx-icon-btn" onClick={onToggle} title="منو"><MenuOutlined /></button>
            <h2 className="gx-topbar-title">{current.label}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <NotificationBell />
            <GuideButton />
            <button className="gx-icon-btn" onClick={toggle} title={mode === "dark" ? "حالت روشن" : "حالت تیره"}>
              {mode === "dark" ? <BulbFilled style={{ color: "#FFD666" }} /> : <BulbOutlined />}
            </button>
            <div className="gx-user">
              <span className="gx-user-avatar">{isAuthed && user ? user.full_name.charAt(0) : "م"}</span>
              <span className="gx-user-name">{isAuthed && user ? user.full_name : "مهمان"}</span>
            </div>
          </div>
        </header>

        <main className="gx-content"><Outlet /></main>
        <MaintenanceWatcher />
      </div>
    </div>
  );
}
